<template>
  <div class="grid grid-cols-2 gap-2">
    <button
      v-for="option in options"
      :key="`${name}-${option.value}`"
      :disabled="isSaving"
      :class="[
        'relative overflow-hidden rounded-lg border-2 transition-all duration-200',
        isSelected(option.value) ? selectedCardClass : defaultCardClass
      ]"
      @click="emit('select', option.value)"
    >
      <div class="p-2 text-center">
        <div class="mx-auto w-6 h-6 mb-1 flex items-center justify-center">
          <svg
            class="w-full h-full text-gray-600 dark:text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
            />
          </svg>
        </div>
        <div :class="['text-lg font-bold', isSelected(option.value) ? selectedTextClass : 'text-gray-900 dark:text-white']">
          {{ option.label }}
        </div>
        <div
          :class="[
            'text-[9px] font-medium uppercase tracking-wide',
            isSelected(option.value) ? selectedSubTextClass : 'text-gray-500 dark:text-gray-400'
          ]"
        >
          {{ option.description }}
        </div>
      </div>

      <div
        v-if="isSelected(option.value)"
        class="absolute top-1 right-1 sm:top-1.5 sm:right-1.5"
      >
        <svg
          class="w-3.5 h-3.5 sm:w-4 sm:h-4"
          :class="selectedSubTextClass"
          fill="currentColor"
          viewBox="0 0 20 20"
        >
          <path
            fill-rule="evenodd"
            d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
            clip-rule="evenodd"
          />
        </svg>
      </div>
    </button>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

interface PaperWidthOption {
  value: number
  label: string
  description: string
}

const props = defineProps<{
  name: string
  options: ReadonlyArray<PaperWidthOption>
  currentWidth: number
  isSaving: boolean
  accent: 'indigo' | 'green'
}>()

const emit = defineEmits<{
  (e: 'select', value: number): void
}>()

const isSelected = (value: number) => props.currentWidth === value

const selectedCardClass = computed(() =>
  props.accent === 'indigo'
    ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20'
    : 'border-green-500 bg-green-50 dark:bg-green-900/20'
)

const selectedTextClass = computed(() =>
  props.accent === 'indigo' ? 'text-indigo-700 dark:text-indigo-300' : 'text-green-700 dark:text-green-300'
)

const selectedSubTextClass = computed(() =>
  props.accent === 'indigo' ? 'text-indigo-600 dark:text-indigo-400' : 'text-green-600 dark:text-green-400'
)

const defaultCardClass =
  'border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800 hover:border-gray-300 dark:hover:border-gray-600'
</script>
